import { useMemo } from 'react';
import { useUserContext } from '../../context/UserContext';
import { calculateStudyParticipantDates } from '../../common/calculateStudyParticipantDates';
import { StudyParticipant } from '../../models/StudyParticipant';
import { StudyStatus } from '../../models/Study';
import { ParticipantEventOccurrence } from '../../screens/study/helpers/ParticipantEventHelpers';

export function useHasActiveStudy() {
  const { studyParticipants, allOccurrences } = useUserContext();

  return useMemo(
    () => hasActiveStudy(studyParticipants ?? [], allOccurrences),
    [allOccurrences, studyParticipants]
  );
}

// Eligible = not completed, paused, or disqualified.
export const isEligibleStudyParticipant = (
  studyParticipant: StudyParticipant
) => {
  const dates = calculateStudyParticipantDates(
    studyParticipant.study,
    studyParticipant
  );
  return (
    !dates.isCompletedStudy &&
    !studyParticipant.paused &&
    !studyParticipant.disqualified
  );
};

// A study can only be started while Testing or Active, Recruiting.
export const isJoinableStudyParticipant = (
  studyParticipant?: StudyParticipant
) =>
  studyParticipant?.study?.status === StudyStatus.Testing ||
  studyParticipant?.study?.status === StudyStatus.ActiveRecruiting;

// Single prioritized pick shared across the app so selections can't diverge:
// a started study, then a joinable one, then any eligible study.
export const selectPrioritizedStudyParticipant = (
  studyParticipants: StudyParticipant[]
) => {
  const list = studyParticipants || [];
  return (
    list.find(sp => isEligibleStudyParticipant(sp) && !!sp.startDate) ??
    list.find(
      sp => isEligibleStudyParticipant(sp) && isJoinableStudyParticipant(sp)
    ) ??
    list.find(sp => isEligibleStudyParticipant(sp))
  );
};

export const gActiveStudyParticipant = (
  studyParticipants: StudyParticipant[],
  allOccurrences: ParticipantEventOccurrence[]
) => {
  const studyParticipant = selectPrioritizedStudyParticipant(studyParticipants);
  if (allOccurrences.length > 0) {
    return studyParticipant;
  } else {
    return undefined;
  }
};

export const hasActiveStudy = (
  studyParticipants: StudyParticipant[],
  allOccurrences: ParticipantEventOccurrence[]
) => {
  const studyParticipant = gActiveStudyParticipant(
    studyParticipants,
    allOccurrences
  );
  return studyParticipant && allOccurrences.length > 0;
};
